import React, { useState, useEffect } from "react";
import { Modal, Empty, DatePicker, Select } from "antd";
import { useQuery } from "@tanstack/react-query";

import CustomTable from "../../Component/Table";
import { getAllUserPlanLog } from "../../Services/subscription";
import { formatDate } from "../../utils/date";
import { FilterContainer } from "../../Styles/filterStyle";
import ExpandableText from "../../Component/ExpandText";

const { RangePicker } = DatePicker;

// user_credit_plan_logs.type values (see app/models/credit_subscription.py UserCreditPlanLog)
const LOG_TYPE_OPTIONS = [
    "deduction", "grant_initial", "grant_renewal_reset", "grant_topup",
    "grant_form_bonus", "grant_product_change", "grant_free_trial",
    "refund_adjustment", "admin_adjustment", "cancellation", "uncancellation",
    "expiration", "billing_issue", "transfer", "subscription_paused",
    "cron_partial_expiry", "monthly_limit_updated", "superseded", "product_changed",
].map((v) => ({
    label: v.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" "),
    value: v,
}));

const columns = [
    { title: "ID", dataIndex: "id", key: "id" },
    { title: "Plan Slug", dataIndex: "plan_slug", key: "plan_slug", render: (text) => text || "-" },
    {
        title: "Log Type",
        dataIndex: "type",
        key: "type",
        render: (text) =>
            text ? text.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : "-",
    },
    { title: "Source", dataIndex: "source", key: "source", render: (text) => text || "-" },
    { title: "Credits Delta", dataIndex: "credits_delta", key: "credits_delta" },
    { title: "Credits Balance After", dataIndex: "credits_balance_after", key: "credits_balance_after" },
    { title: "Category", dataIndex: "category_name", key: "category_name", render: (text) => text || "-" },
    { title: "Itinerary Id", dataIndex: "itineary_id", key: "itineary_id", render: (text) => text || "-" },
    { title: "Log Description", dataIndex: "description", key: "description", render: (text) => <ExpandableText text={text} />, },
    {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        render: (text) => (text ? formatDate(text) : "-"),
    },
];

function UserLogModal({ isModalVisible, handleCancel, initialValues }) {
    const [selectedUserId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedLogType, setSelectedLogType] = useState(null);
    const [dateRange, setDateRange] = useState({
        start_date: null,
        end_date: null,
    });

    const skip = (currentPage - 1) * pageSize;

    // Reset to first page when search term or selected user changes
    useEffect(() => {
        setCurrentPage(1);
    }, [ selectedUserId, selectedLogType, dateRange]);

    const { data, isLoading } = useQuery({
        queryKey: [`user-plan-logs-${initialValues?.id}`, currentPage, pageSize,  selectedUserId, selectedLogType, dateRange],
        queryFn: () => getAllUserPlanLog({ userId: initialValues?.id,
            name: null,
            costType: selectedLogType,
            skip,
            limit: pageSize,
            start_date: dateRange.start_date,
            end_date: dateRange.end_date,
            isCreditPlan: true,
        }),
        enabled: !!initialValues?.id,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const handleDateChange = (dates) => {
        if (dates) {
            setDateRange({
                start_date: dates[0].format("YYYY-MM-DD"),
                end_date: dates[1].format("YYYY-MM-DD"),
            });
        } else {
            setDateRange({
                start_date: null,
                end_date: null,
            });
        }
    };

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    return (
        <Modal
            title={`Plan Logs`}
            open={isModalVisible}
            footer={null}
            onCancel={handleCancel}
            width={800}
        >
            <FilterContainer style={{ marginBottom: 16, display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <Select
                allowClear
                placeholder="Select Log Type"
                style={{ width: 190 }}
                value={selectedLogType}
                onChange={setSelectedLogType}
                options={LOG_TYPE_OPTIONS}
                />

                <RangePicker onChange={handleDateChange} />
            </FilterContainer>

            {(data?.logs?.length || isLoading) ? (
                <CustomTable
                    columns={columns}
                    data={data?.logs || []}
                    loading={isLoading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: data?.total_count || 0,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        position: ["bottomRight"],
                    }}
                    onTableChange={handleTableChange}
                />
            ) : !isLoading ? (
            <Empty description="No Logs Found" />
            ) : null}

        </Modal>
    );
}

export default UserLogModal;
